import React, { useState, useEffect } from "react";
import axiosInstance from "../../../../../api";

// Component for managing notification settings and contact email templates
const NotificationSettings = () => {
  // State for storing users fetched from the API
  const [users, setUsers] = useState([]);
  // State for storing alarms fetched from the API
  const [alarms, setAlarms] = useState([]);
  // State for selected alarm and notification settings
  const [selectedAlarm, setSelectedAlarm] = useState("");
  const [emailNotif, setEmailNotif] = useState(false);
  const [webNotif, setWebNotif] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  // State for notification and contact variables
  const [variables, setVariables] = useState([]);
  const [contactVariables, setContactVariables] = useState([]);
  // State for contact email template
  const [contactSubject, setContactSubject] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  // State for loading indicators
  const [loadingAlarm, setLoadingAlarm] = useState(false);
  const [loadingContact, setLoadingContact] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  // State for saved settings and recipients
  const [savedSettings, setSavedSettings] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [newEmailRecipient, setNewEmailRecipient] = useState("");
  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [currentAlarmPage, setCurrentAlarmPage] = useState(1);
  const perPage = 5;

  // Fetch users for recipient management
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axiosInstance.get("/ems/users-management");
        if (response.data.status === "success") {
          setUsers(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, []);

  // Fetch configurable alarms from API
  useEffect(() => {
    const fetchAlarms = async () => {
      try {
        const response = await axiosInstance.get("/ems/get-configural-alarm");
        if (response.data.status === "success") {
          setAlarms(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching configurable alarms:", error);
      }
    };
    fetchAlarms();
  }, []);

  // Fetch notification variables for templates
  useEffect(() => {
    const fetchVars = async () => {
      try {
        const res = await axiosInstance.get(
          "/ems/get-notification-variables/Notification"
        );
        if (res.data.status === "success") {
          setVariables(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching notification variables:", err);
      }
    };
    fetchVars();
  }, []);

  // Fetch contact variables for email templates
  useEffect(() => {
    const fetchContactVars = async () => {
      try {
        const res = await axiosInstance.get(
          "/ems/get-notification-variables/contact");
        if (res.data.status === "success") {
          setContactVariables(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching contact variables:", err);
      }
    };
    fetchContactVars();
  }, []);

  // Fetch saved notification settings
  useEffect(() => {
    const fetchSavedSettings = async () => {
      try {
        const res = await axiosInstance.get("/ems/get-notification-setting");
        if (res.data.status === "success") {
          setSavedSettings(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching saved settings:", err);
      }
    };
    fetchSavedSettings();
  }, []);

  // Fetch contact email template
  useEffect(() => {
    const fetchEmailTemplate = async () => {
      try {
        const res = await axiosInstance.get("/ems/get-email-template");
        if (res.data.status === "success") {
          const { EmsNotificationRecipient, EmsNotificationTemplate } = res.data;
          // Set recipients from API response
          if (EmsNotificationRecipient && EmsNotificationRecipient.length > 0) {
            setRecipients(EmsNotificationRecipient.map((r) => r.email));
          }
          // Set template subject and message
          if (EmsNotificationTemplate && EmsNotificationTemplate.length > 0) {
            setContactSubject(EmsNotificationTemplate[0].subject || "");
            setContactMessage(EmsNotificationTemplate[0].message || "");
          }
        }
      } catch (err) {
        console.error("Error fetching email template:", err);
      }
    };
    fetchEmailTemplate();
  }, []);

  // Fetch details for a selected alarm
  const fetchSingleAlarm = async (alarmName) => {
    if (!alarmName) {
      setEmailNotif(false);
      setWebNotif(false);
      setSelectedRecipients([]);
      setSubject("");
      setMessage("");
      return;
    }
    try {
      const res = await axiosInstance.get("/ems/get-notification-setting");
      if (res.data.status === "success") {
        const alarm = res.data.data.find(
          (setting) => setting.alarm_name === alarmName
        );
        if (alarm) {
          setSelectedAlarm(alarm.alarm_name);
          setEmailNotif(alarm.email_notifications === 1);
          setWebNotif(alarm.web_notifications === 1);
          setSelectedRecipients(
            alarm.recipients.map((r) => ({
              user_id: r.user_id,
              email: r.email,
            })) || []
          );
          if (alarm.templates && alarm.templates.length > 0) {
            setSubject(alarm.templates[0].subject);
            setMessage(alarm.templates[0].message);
          } else {
            setSubject("");
            setMessage("");
          }
        } else {
          setEmailNotif(false);
          setWebNotif(false);
          setSelectedRecipients([]);
          setSubject("");
          setMessage("");
        }
      }
    } catch (err) {
      console.error("Error fetching single alarm:", err);
      setEmailNotif(false);
      setWebNotif(false);
      setSelectedRecipients([]);
      setSubject("");
      setMessage("");
    }
  };

  // Insert variable into subject or message field
  const insertVariable = (field, variable) => {
    if (field === "subject") {
      setSubject((prev) => prev + " " + variable);
    } else {
      setMessage((prev) => prev + " " + variable);
    }
  };

  // Insert contact variable into contact subject or message field
  const insertContactVariable = (field, variable) => {
    if (field === "subject") {
      setContactSubject((prev) => prev + " " + variable);
    } else {
      setContactMessage((prev) => prev + " " + variable);
    }
  };

  // Toggle recipient selection
  const toggleRecipient = (user) => {
    setSelectedRecipients((prev) => {
      if (prev.find((r) => r.user_id === user.id)) {
        return prev.filter((r) => r.user_id !== user.id);
      } else {
        return [...prev, { user_id: user.id, email: user.email }];
      }
    });
  };

  // Save notification settings for selected alarm
  const saveNotification = async () => {
    if (!selectedAlarm) {
      alert("Please select an alarm before saving.");
      return;
    }
    setLoadingAlarm(true);
    try {
      const payload = {
        alarm_name: selectedAlarm,
        email_notifications: emailNotif,
        web_notifications: webNotif,
        recipients: selectedRecipients.map((r) => ({
          id: r.user_id,
          email: r.email,
        })),
        subject,
        message,
      };

      const res = await axiosInstance.post(
        "/ems/save-notification-setting",
        payload
      );

      if (res.data.status === "success") {
        alert("Notification setting saved successfully!");
        const settingsRes = await axiosInstance.get(
          "/ems/get-notification-setting");
        if (settingsRes.data.status === "success") {
          setSavedSettings(settingsRes.data.data);
        }
      } else {
        alert("Failed to save notification setting.");
      }
    } catch (err) {
      console.error("Error saving notification:", err);
      alert("Error while saving settings.");
    } finally {
      setLoadingAlarm(false);
    }
  };

  // Save contact email template
  const saveContactTemplate = async () => {
    if (!contactSubject || !contactMessage || recipients.length === 0) {
      alert("Please fill in subject, message, and add at least one recipient.");
      return;
    }
    setLoadingContact(true);
    try {
      const payload = {
        recipients: recipients.map((email) => ({ email })),
        subject: contactSubject,
        message: contactMessage,
      };

      const res = await axiosInstance.post(
        "/ems/save-email-template",
        payload
      );

      if (res.data.status === "success") {
        alert("Contact email template saved successfully!");
        setContactSubject("");
        setContactMessage("");
        setRecipients([]);
      } else {
        alert("Failed to save contact email template.");
      }
    } catch (err) {
      console.error("Error saving contact template:", err);
      alert("Error while saving contact template.");
    } finally {
      setLoadingContact(false);
    }
  };

  // Delete a notification setting
  const deleteNotification = async (settingId) => {
    setLoadingDelete(true);
    try {
      const res = await axiosInstance.get(
        `/ems/delete-active-alarm/${settingId}`
      );
      if (res.data.status === "success") {
        alert("Notification setting deleted successfully!");
        setSavedSettings(
          savedSettings.filter((setting) => setting.id !== settingId)
        );
        if (currentAlarmPage > Math.ceil(savedSettings.length / perPage)) {
          setCurrentAlarmPage(1);
        }
      } else {
        alert("Failed to delete notification setting.");
      }
    } catch (err) {
      console.error("Error deleting notification:", err.response?.data || err.message);
      alert("Error while deleting settings: " + (err.response?.data?.message || err.message));
    } finally {
      setLoadingDelete(false);
    }
  };

  // Add a new recipient email
  const addRecipient = () => {
    if (newEmailRecipient.trim() !== "") {
      setRecipients([...recipients, newEmailRecipient.trim()]);
      setNewEmailRecipient("");
    }
  };

  // Remove a recipient email
  const removeRecipient = (index) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  // Pagination logic for recipient management
  const indexOfLastUser = currentPage * perPage;
  const indexOfFirstUser = indexOfLastUser - perPage;
  const currentUsers = users.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(users.length / perPage);

  // Pagination logic for active alarms
  const indexOfLastAlarm = currentAlarmPage * perPage;
  const indexOfFirstAlarm = indexOfLastAlarm - perPage;
  const currentAlarms = savedSettings.slice(indexOfFirstAlarm, indexOfLastAlarm);
  const totalAlarmPages = Math.ceil(savedSettings.length / perPage);

  // Handle page change for recipient pagination
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle page change for active alarms pagination
  const handleAlarmPageChange = (pageNumber) => {
    setCurrentAlarmPage(pageNumber);
  };

  // Render the notification settings interface
  return (
    <div className="tab-pane" id="notification-settings" role="tabpanel">
      <div className="row">
        {/* Left Section: Configurable Alarms and Notification Settings */}
        <div className="col-lg-6 border-end">
          <h6 className="mb-3 fw-bold mt-4">Configurable Alarms</h6>
          <div className="mb-3">
            <select
              className="form-select"
              value={selectedAlarm}
              onChange={(e) => {
                setSelectedAlarm(e.target.value);
                fetchSingleAlarm(e.target.value);
              }}
            >
              <option value="">Select Alarm</option>
              {alarms.map((alarm) => (
                <option key={alarm.id} value={alarm.alarm_name}>
                  {alarm.alarm_name}
                </option>
              ))}
            </select>
          </div>

          <h6 className="mt-5">Notification Channels</h6>
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              checked={emailNotif}
              onChange={(e) => setEmailNotif(e.target.checked)}
            />
            <label className="form-check-label">Email Notifications</label>
          </div>
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              checked={webNotif}
              onChange={(e) => setWebNotif(e.target.checked)}
            />
            <label className="form-check-label">Web-page Notifications</label>
          </div>

          <h6 className="mt-5">Recipient Management</h6>
          <table className="table">
            <thead>
              <tr>
                <th>Select</th>
                <th>Name</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={selectedRecipients.some(
                        (r) => r.user_id === user.id
                      )}
                      onChange={() => toggleRecipient(user)}
                    />
                  </td>
                  <td>
                    {user.first_name} {user.last_name}
                  </td>
                  <td>{user.email}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="Recipient pagination">
            <ul className="pagination">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>
              {[...Array(totalPages).keys()].map((page) => (
                <li
                  key={page + 1}
                  className={`page-item ${
                    currentPage === page + 1 ? "active" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(page + 1)}
                  >
                    {page + 1}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>

          <h6 className="mt-5">Notification Templates</h6>
          <label>Subject:</label>
          {variables.map((v, i) => (
            <button
              key={i} 
              type="button"
              className="btn btn-sm mx-3 mb-1 btn-outline-primary"
              onClick={() => insertVariable("subject", v)}
            >
              {v}
            </button>
          ))}
          <div className="d-flex gap-2 mb-2">
            <input
              type="text"
              className="form-control"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Notification Subject"
            />
          </div>
          <label>Message:</label>
          {variables.map((v, i) => (
            <button
              key={i}
              type="button"
              className="btn btn-sm mx-3 mb-1 btn-outline-primary mb-1"
              onClick={() => insertVariable("message", v)}
            >
              {v}
            </button>
          ))}
          <div className="d-flex gap-2 mb-2">
            <textarea
              className="form-control"
              rows="3"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Message Body"
            />
          </div>
          <button
            className="btn btn-outline-primary"
            onClick={saveNotification}
            disabled={loadingAlarm}
          >
            {loadingAlarm ? (
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
            ) : null}
            {loadingAlarm ? "Saving..." : "Save"}
          </button>
        </div>

        {/* Right Section: Contact Us Email Template */}
        <div className="col-lg-6">
          <h6 className="mt-5">Contact Us</h6>
          <h6 className="mt-5">Recipient Manager</h6>
          <div className="d-flex align-items-center mb-2">
            <input
              type="email"
              className="form-control me-2"
              placeholder="Enter recipient email"
              value={newEmailRecipient}
              onChange={(e) => setNewEmailRecipient(e.target.value)}
            />
            <button
              type="button"
              className="btn p-0 border-0 bg-transparent text-primary"
              onClick={addRecipient}
            >
              <i className="ti ti-circle-plus fs-4"></i>
            </button>
          </div>
          {recipients
            .filter((email) => email.trim() !== "")
            .map((email, index) => (
              <div
                key={index}
                className="d-flex align-items-center mb-2"
              >
                <span className="flex-grow-1">{email}</span>
                <button
                  type="button"
                  className="btn p-0 border-0 bg-transparent ms-2"
                  onClick={() => removeRecipient(index)}
                >
                  <i className="ti ti-trash text-danger fs-5"></i>
                </button>
              </div>
            ))}

          <h6 className="mt-5">Notification Templates</h6>
          <label>Subject:</label>
          {contactVariables.map((v, i) => (
            <button
              key={i}
              type="button"
              className="btn btn-sm mx-3 mb-1 btn-outline-primary"
              onClick={() => insertContactVariable("subject", v)}
            >
              {v}
            </button>
          ))}
          <input
            type="text"
            className="form-control mb-2"
            placeholder="Enter Subject"
            value={contactSubject}
            onChange={(e) => setContactSubject(e.target.value)}
          />
          <label>Message Body:</label>
          {contactVariables.map((v, i) => (
            <button
              key={i}
              type="button"
              className="btn btn-sm mx-3 mb-1 btn-outline-primary"
              onClick={() => insertContactVariable("message", v)}
            >
              {v}
            </button>
          ))}
          <textarea
            className="form-control mb-2"
            rows="3"
            placeholder="Enter Message"
            value={contactMessage}
            onChange={(e) => setContactMessage(e.target.value)}
          />
          <button
            className="btn btn-outline-primary"
            onClick={saveContactTemplate}
            disabled={loadingContact}
          >
            {loadingContact ? (
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
            ) : null}
            {loadingContact ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* Active Alarms Table */}
      <h6 className="mt-5">Active Alarms</h6>
      <table className="table">
        <thead>
          <tr>
            <th>Alarm</th>
            <th>Notification Channel</th>
            <th>Recipients</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {currentAlarms.map((setting) => (
            <tr key={setting.id}>
              <td>{setting.alarm_name}</td>
              <td>
                {setting.email_notifications && setting.web_notifications
                  ? "Email, Web"
                  : setting.email_notifications
                  ? "Email"
                  : setting.web_notifications
                  ? "Web"
                  : "None"}
              </td>
              <td>{setting.recipients?.map((r) => r.email).join(", ") || "-"}</td>
              <td>
                <button
                  type="button"
                  className="btn p-0 border-0 bg-transparent"
                  onClick={() => deleteNotification(setting.id)}
                  disabled={loadingDelete}
                >
                  <i className="ti ti-trash text-danger fs-5"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination for Active Alarms */}
      <nav aria-label="Active Alarms pagination">
        <ul className="pagination">
          <li
            className={`page-item ${currentAlarmPage === 1 ? "disabled" : ""}`}
          >
            <button
              className="page-link"
              onClick={() => handleAlarmPageChange(currentAlarmPage - 1)}
              disabled={currentAlarmPage === 1}
            >
              Previous
            </button>
          </li>
          {[...Array(totalAlarmPages).keys()].map((page) => (
            <li
              key={page + 1}
              className={`page-item ${
                currentAlarmPage === page + 1 ? "active" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handleAlarmPageChange(page + 1)}
              >
                {page + 1}
              </button>
            </li>
          ))}
          <li
            className={`page-item ${
              currentAlarmPage === totalAlarmPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handleAlarmPageChange(currentAlarmPage + 1)}
              disabled={currentAlarmPage === totalAlarmPages}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

// Export the component as default
export default NotificationSettings;